import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, Table, useReactTable } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { LlmModelData } from '../../../../api/endpoints/llm';
import { Button, Colors, Menu, remInPx } from '../../../../ui';
import { Checkbox } from '../../../../ui/Checkbox';
import { LoadingGuard } from '../../../../ui/LoadingGuard';
import { MenuButton } from '../../../../ui/MenuButton';
import { useLocationScope } from '../../../AppLocation';
import { useResolve } from '../../../Container';
import { DataTable } from '../../../common/DataTable';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { TaskTableTheme } from '../../../dashboard/tasks/TaskTableTheme';
import { usePopup } from '../../../popups/PopupHooks';
import { ManageFrame } from '../ManageFrame';
import { BadgeCell, TextCell } from '../common/TableElements';
import { addProviderPopup } from './AddProviderPopup';
import { useOnBulkDeleteLlmInstances } from './ManageAIActions';

// LLM Table Component
const columnHelper = createColumnHelper<LlmModelData>();

const columns = [
    columnHelper.display({
        id: 'select',
        size: remInPx(5),
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected()
                }
                onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
            />
        ),
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row.provider.label, {
        id: 'providerLabel',
        size: remInPx(12),
        header: () => <span>Provider</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor('modelName', {
        size: remInPx(16),
        header: () => <span>Model</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => {
        const date = new Date(row.createdAt);
        return date.toLocaleDateString();
    }, {
        id: 'createdAt',
        size: remInPx(12),
        header: () => <span>Created</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor('enabled', {
        size: remInPx(10),
        header: () => <span>Status</span>,
        cell: info => {
            const enabled = info.getValue();
            return enabled
                ? <BadgeCell color={Colors.positive} value="Enabled" />
                : <BadgeCell color={Colors.gray200} value="Disabled" />;
        }
    }),
    columnHelper.display({
        id: 'null',
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
    }),
];

const useSelection = <T extends { id: string }>(table: Table<T>) => {
    const { rows } = table.getRowModel();
    return rows.filter(x => x.getIsSelected()).map(x => x.original);
};

export type LlmTableViewProps = {
    items: LlmModelData[],
    onSelectionChange: (selection: LlmModelData[]) => void,
};

const LlmTableView = React.memo(({ items, onSelectionChange }: LlmTableViewProps) => {

    useEffect(() => {
        table.toggleAllRowsSelected(false);
    }, [items]);

    const table = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: true,
    });

    const selection = useSelection(table);
    useEffect(() => {
        onSelectionChange(selection);
    }, [selection]);

    return <DataTable table={table} selectFromFirstCellOnly theme={TaskTableTheme} />;
});

const BulkActionMenu = ({ selection }: { selection: LlmModelData[] }) => {

    const [open, setOpen] = useState(false);
    const setClosed = useCallback(() => setOpen(false), []);

    const modelIds = useMemo(() => selection.map(x => x.id), [selection]);
    const onBulkRemoveUserFromAccount = useOnBulkDeleteLlmInstances(modelIds, setClosed);

    const label = !!selection.length
        ? `Bulk Actions (${selection.length})`
        : 'Bulk Actions';

    return <MenuButton disabled={!selection.length} label={label} {...{ open, setOpen }}>
        <Menu>
            <Menu.Section first>
                <Menu.Item onClick={onBulkRemoveUserFromAccount}>
                    <Menu.ItemText>
                        Remove Models
                    </Menu.ItemText>
                </Menu.Item>
            </Menu.Section>
        </Menu>
    </MenuButton>;
};

// Main component
const useLlmModelsQuery = () => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['llm.instances.list'],
        queryFn: async () => api.exec(v2.llm.instances.list()),
    });
};

export const ManageAIView = () => {
    useLocationScope('ai', 'overview');

    const [selection, setSelection] = useState([] as LlmModelData[]);
    const { data, isLoading } = useLlmModelsQuery();
    const { showPopup } = usePopup();

    const onAddProvider = useCallback(() => {
        showPopup(addProviderPopup());
    }, [showPopup]);

    return <ManageFrame title="AI Settings">
        <Flexer flex="row" gap="1" align="center" padding="1 0">
            <BulkActionMenu selection={selection} />
            <Button variant="primary" onAction={onAddProvider}>
                + Add Models
            </Button>
        </Flexer>
        <LoadingGuard loading={isLoading}>
            {data && <LlmTableView items={data} onSelectionChange={setSelection} />}
        </LoadingGuard>
    </ManageFrame>;
};
